import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GameInstructions = ({ 
  selectedGame = null,
  onClose = () => {},
  onStartGame = () => {},
  className = "" 
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Default game instructions if none provided
  const defaultInstructions = {
    'reaction-time': {
      title: 'Reaction Time Test',
      description: 'Measure how quickly you can respond to visual stimuli',
      steps: [
        {
          title: 'Get Ready',
          description: 'Position yourself comfortably in front of your device',
          icon: 'User',
          details: [
            'Sit in a comfortable position',
            'Ensure good lighting',
            'Remove distractions',
            'Use your dominant hand'
          ]
        },
        {
          title: 'Watch for the Signal',
          description: 'A green circle will appear after a random delay',
          icon: 'Eye',
          details: [
            'Keep your eyes on the screen',
            'Wait for the green circle',
            'Do not click before it appears',
            'Stay focused and alert'
          ]
        },
        {
          title: 'React Quickly',
          description: 'Click or tap as soon as you see the green circle',
          icon: 'Zap',
          details: [
            'Click immediately when you see green',
            'Use a single quick tap/click',
            'Avoid multiple clicks',
            'Your time will be recorded'
          ]
        },
        {
          title: 'Complete All Attempts',
          description: 'You will complete 5 attempts for an average score',
          icon: 'Target',
          details: [
            'Complete all 5 attempts',
            'Each attempt is independent',
            'Your best and average times are recorded',
            'Results are saved automatically'
          ]
        }
      ],
      tips: [
        'Stay relaxed but alert',
        'Focus on the center of the screen',
        'Don\'t anticipate - wait for the signal',
        'Practice regularly to improve'
      ]
    },
    'memory': {
      title: 'Memory Sequence Test',
      description: 'Test your ability to remember and repeat sequences',
      steps: [
        {
          title: 'Watch the Sequence',
          description: 'Observe the pattern of highlighted elements',
          icon: 'Eye',
          details: [
            'Pay attention to the order',
            'Watch each element carefully',
            'Don\'t try to click during display',
            'Focus on the sequence pattern'
          ]
        },
        {
          title: 'Repeat the Pattern',
          description: 'Click the elements in the same order',
          icon: 'MousePointer',
          details: [
            'Click in the exact same order',
            'Take your time to remember',
            'One mistake ends the sequence',
            'Sequences get progressively longer'
          ]
        }
      ],
      tips: [
        'Use memory techniques like chunking',
        'Visualize the pattern',
        'Stay calm under pressure',
        'Practice improves memory capacity'
      ]
    }
  };

  const gameInstructions = selectedGame ? defaultInstructions?.[selectedGame?.type] || defaultInstructions?.['reaction-time'] : defaultInstructions?.['reaction-time'];

  const nextStep = () => {
    if (currentStep < gameInstructions?.steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = gameInstructions?.steps?.[currentStep];

  return (
    <div className={`fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4 ${className}`}>
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground">{gameInstructions?.title}</h2>
              <p className="text-sm text-muted-foreground mt-1">{gameInstructions?.description}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
            />
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              Step {currentStep + 1} of {gameInstructions?.steps?.length}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round(((currentStep + 1) / gameInstructions?.steps?.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / gameInstructions?.steps?.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Current Step Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon 
                name={currentStepData?.icon} 
                size={32} 
                color="var(--color-primary)"
              />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {currentStepData?.title}
            </h3>
            <p className="text-muted-foreground">
              {currentStepData?.description}
            </p>
          </div>

          {/* Step Details */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <ul className="space-y-2">
              {currentStepData?.details?.map((detail, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon 
                    name="Check" 
                    size={16} 
                    color="var(--color-success)"
                    className="mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-foreground">{detail}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Tips Section (shown on last step) */}
          {currentStep === gameInstructions?.steps?.length - 1 && (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Lightbulb" size={18} color="var(--color-accent)" />
                <span className="font-medium text-foreground">Pro Tips</span>
              </div>
              <ul className="space-y-1">
                {gameInstructions?.tips?.map((tip, index) => (
                  <li key={index} className="text-sm text-foreground flex items-start space-x-2">
                    <span className="text-accent font-bold">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="p-6 border-t border-border">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              iconName="ChevronLeft"
              iconPosition="left"
            >
              Previous
            </Button>

            <div className="flex space-x-2">
              {gameInstructions?.steps?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2 h-2 rounded-full transition-colors duration-150 ${
                    index === currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            {currentStep < gameInstructions?.steps?.length - 1 ? (
              <Button
                variant="default"
                onClick={nextStep}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => {
                  onClose();
                  onStartGame(selectedGame);
                }}
                iconName="Play"
                iconPosition="left"
              >
                Start Game
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameInstructions;